import { Component, OnInit } from '@angular/core';
import { NgWizardConfig, THEME, StepChangedArgs, StepValidationArgs, NgWizardService, TOOLBAR_POSITION } from 'ng-wizard';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { ActivatedRoute, Router } from '@angular/router';
import { CrmService } from 'app/services/crm.service';
import { RemoteAssessment } from 'app/model/remote-assessment';
import { Lookup } from 'app/model/lookup';
import { ContextService } from 'app/services/context.service';
import { ToastService } from 'app/services/toast.service';


@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit {

  assessmentid = "";
  userId = "";
  userName = "";
  pharmacyName = "";
  remoteAssessment: RemoteAssessment;
  @BlockUI() blockUI: NgBlockUI;
  token: string;
  isPostStepThreeReadOnlyWorkflow = false;
  config: NgWizardConfig = {
    selected: 0,
    theme: THEME.arrows,
    toolbarSettings: {
      toolbarExtraButtons: [
        { text: 'Finish', class: 'btn btn-info', event: () => { alert('Finished!!!'); } }],
      toolbarPosition: TOOLBAR_POSITION.none
    }
  };

  private getEosCancelledNoOutOfLevelKey(): string {
    const assessmentId = this.remoteAssessment?.assessment?.id || this.assessmentid || '';
    return `EOS_CANCELLED_NO_OUT_OF_LEVEL_${assessmentId}`;
  }

  private clearEosCancelledNoOutOfLevelFlag(): void {
    sessionStorage.removeItem(this.getEosCancelledNoOutOfLevelKey());
  }

  private clearPersistedEosCancelledNoOutOfLevelFlag(): void {
    localStorage.removeItem(this.getEosCancelledNoOutOfLevelKey());
  }



  private getIsEosCancelledNoOutOfLevelFlag(): boolean {
    const key = this.getEosCancelledNoOutOfLevelKey();
    return sessionStorage.getItem(key) === 'true' || localStorage.getItem(key) === 'true';
  }

  private getPharmacyLevelsSet(): Set<number> {
    return new Set(
      (this.remoteAssessment?.pharmacyDetails?.servicelevel || [])
        .map(level => Number(level?.value))
        .filter(value => !isNaN(value))
    );
  }

  private getHasOutOfLevelSelectionsFromCurrentState(): boolean {
    const pharmacyLevels = this.getPharmacyLevelsSet();
    return (this.remoteAssessment?.professionalServices || []).some(ps =>
      ps?.selected === true &&
      ps?.servicelevel?.id != null &&
      !pharmacyLevels.has(Number(ps.servicelevel.id))
    );
  }

  private getHasPsd09aLevelsFromCurrentState(): boolean {
    const pharmacyLevels = this.getPharmacyLevelsSet();
    const allServices = this.remoteAssessment?.professionalServices || [];

    const activeAccreditedServiceIds = new Set(
      (this.remoteAssessment?.assessmentPS || [])
        .filter(aps => String(aps?.status?.id ?? '') === '0')
        .map(aps => aps?.serviceName?.id)
        .filter(id => !!id)
    );

    return Array.from(pharmacyLevels).some(levelId => {
      const accreditedAtLevel = allServices.filter(ps =>
        activeAccreditedServiceIds.has(ps?.id) &&
        ps?.servicelevel?.id != null &&
        Number(ps.servicelevel.id) === levelId
      );
 
      const hasSelectedNonAccreditedAtLevel = allServices.some(ps =>
        ps?.selected === true &&
        !activeAccreditedServiceIds.has(ps?.id) &&
        ps?.servicelevel?.id != null &&
        Number(ps.servicelevel.id) === levelId
      );

      return accreditedAtLevel.length > 0 &&
        !accreditedAtLevel.some(ps => ps?.selected === true) &&
        !hasSelectedNonAccreditedAtLevel;
    });
  }

  get step4Description(): string {
    const isEos = String(this.remoteAssessment?.assessment?.assessmentType?.id || '') === '806390002';
    const isLocked = this.remoteAssessment?.assessment?.isPSDLocked === true;
    const hasOutOfLevelSelections = this.getHasOutOfLevelSelectionsFromCurrentState();
    const hasPsd09aLevels = this.getHasPsd09aLevelsFromCurrentState();
    return isEos && isLocked && this.getIsEosCancelledNoOutOfLevelFlag() && !hasOutOfLevelSelections && !hasPsd09aLevels
      ? 'Finished'
      : 'Submit Your Assessment';
  }

  // Pre-navigation guard for Step 1/2 when PSD is locked.
  canEnterPrePSDSteps = (args: StepValidationArgs): boolean => {
    if (!this.remoteAssessment?.assessment?.isPSDLocked || this.isPostStepThreeReadOnlyWorkflow) {
      return true;
    }

    return !(args?.fromStep?.index >= 2 && args?.toStep?.index < 2);
  }

  constructor(private ngWizardService: NgWizardService, private route: ActivatedRoute, private router: Router, private crmService: CrmService, private context: ContextService, private toastService: ToastService) {

  }

  private setSessionValues(assessmentId: string, userId: string, userName: string, pharmacyName: string, assessmentType?: string) {
    this.assessmentid = assessmentId || "";
    this.userId = userId || "";
    this.userName = userName || "";
    this.pharmacyName = pharmacyName || "";

    localStorage.setItem('AssessmentId', this.assessmentid);
    localStorage.setItem('UserId', this.userId);
    localStorage.setItem('UserName', this.userName);
    localStorage.setItem('PharmacyName', this.pharmacyName);
    if (assessmentType) {
      localStorage.setItem('assessmentType', assessmentType);
    }
  }

  private loadAssessmentDetails() {
    // Fresh page load must default Step 4 wizard header to submit mode.
    // The cancel header is set only after the user executes cancel flow in Step 2.
    this.clearEosCancelledNoOutOfLevelFlag();

    this.context.updateContextParams([this.assessmentid, this.userId, this.userName, this.pharmacyName]);
    this.blockUI.start('Loading...');
    let RASub = this.crmService.GetAssessmentDetails(this.assessmentid, this.userId).subscribe(ra => {
      this.remoteAssessment = ra;
      if (ra.assessment.submitted) {
        this.remoteAssessment.readOnly = true;
      }
      if (!ra.isQuestionAvilable && ra.isQuestionAvilableMSG == "Assessment expired.") {
        this.remoteAssessment.readOnly = true;
      }
      if (Number(ra.assessment?.statuscode?.id) === 1) {
        this.remoteAssessment.readOnly = true;
      }

      this.isPostStepThreeReadOnlyWorkflow = this.remoteAssessment?.assessment?.isPSDLocked === true
        && this.remoteAssessment?.readOnly !== true;
      this.context.updatePostStepThreeReadOnlyWorkflow(this.isPostStepThreeReadOnlyWorkflow);

      if (!ra.isQuestionAvilable && ra.isQuestionAvilableMSG == "Assessment Not Available Yet") {
        this.router.navigate(['/not-available']);
      }
      var assessmentLookup = new Lookup(this.assessmentid, "ecs_assessment");
      var contactLookup = new Lookup(this.userId, "contact");
      if (!this.remoteAssessment.readOnly) {
        this.toastService.showInfo("You can close this screen at any time and your progress will be saved.");
      }
      this.remoteAssessment.assessmentLookup = assessmentLookup;
      this.remoteAssessment.contactLookup = contactLookup;

      // If backend still reports assessment in-progress (PSD not locked),
      // clear any previously persisted cancel marker.
      if (!this.remoteAssessment?.assessment?.isPSDLocked) {
        this.clearEosCancelledNoOutOfLevelFlag();
        this.clearPersistedEosCancelledNoOutOfLevelFlag();
      }

      this.context.updateContextRA(this.remoteAssessment);

      const shouldShowCancelledStep =
        String(this.remoteAssessment?.assessment?.assessmentType?.id || '') === '806390002' &&
        this.remoteAssessment?.assessment?.isPSDLocked === true &&
        this.getIsEosCancelledNoOutOfLevelFlag() &&
        !this.getHasOutOfLevelSelectionsFromCurrentState() &&
        !this.getHasPsd09aLevelsFromCurrentState();

      if (shouldShowCancelledStep) {
        this.ngWizardService.show(3);
      } else if (this.remoteAssessment?.assessment?.isPSDLocked) {
        this.ngWizardService.show(2);
      }
      this.blockUI.stop();
      RASub.unsubscribe();
    })
  }

  ngOnInit(): void {

    this.context.updateWizardStepIndex(0);
    this.context.updatePostStepThreeReadOnlyWorkflow(false);
    this.isPostStepThreeReadOnlyWorkflow = false;

    this.route.paramMap.subscribe(params => {
      this.token = params.get("token") || this.route.snapshot.queryParamMap.get("token");

      if (this.token) {
        try {
          let paramsArr = window.atob(this.token).split(";");
          if (paramsArr.length >= 2) {
            this.setSessionValues(paramsArr[0], paramsArr[1], paramsArr[2] || "", paramsArr[3] || "", paramsArr[4]);
            if (paramsArr[4] == '806390002') {
              this.router.navigate(['/expansion-of-services']);
            } else {
              this.router.navigate(['/home']);
            }
            return;
          }
        } catch {
          // Fall through to URL/storage fallback.
        }
      } else {
        // No token provided: allow explicit URL IDs, then fall back to localStorage.
        const urlAssessmentId = this.route.snapshot.queryParamMap.get('assessmentId');
        const urlUserId = this.route.snapshot.queryParamMap.get('userId');

        if (urlAssessmentId && urlUserId) {
          const urlUserName = this.route.snapshot.queryParamMap.get('userName') || localStorage.getItem('UserName') || "";
          const urlPharmacyName = this.route.snapshot.queryParamMap.get('pharmacyName') || localStorage.getItem('PharmacyName') || "";
          const urlAssessmentType = this.route.snapshot.queryParamMap.get('assessmentType') || localStorage.getItem('assessmentType');
          this.setSessionValues(urlAssessmentId, urlUserId, urlUserName, urlPharmacyName, urlAssessmentType);
        } else if (localStorage.getItem('AssessmentId') && localStorage.getItem('UserId')) {
          this.assessmentid = localStorage.getItem('AssessmentId');
          this.userId = localStorage.getItem('UserId');
          this.userName = localStorage.getItem('UserName') || "";
          this.pharmacyName = localStorage.getItem('PharmacyName') || "";
        }

        if (this.assessmentid && this.userId) {
          this.loadAssessmentDetails();
        } else {
          this.router.navigate(['/error']);
        }
      }
    });

  }
  showPreviousStep(event?: Event) {
    this.ngWizardService.previous();
  }

  showNextStep(event?: Event) {
    this.ngWizardService.next();
  }

  resetWizard(event?: Event) {
    this.ngWizardService.reset();

  }

  setTheme(theme: THEME) {
    this.ngWizardService.theme(theme);
  }

  stepChanged(args: StepChangedArgs, event?: Event) {
    const currentStepIndex = (args as any)?.step?.index;
    if (typeof currentStepIndex === 'number') {
      this.context.updateWizardStepIndex(currentStepIndex);

      if (currentStepIndex >= 2 && this.remoteAssessment?.readOnly !== true && !this.isPostStepThreeReadOnlyWorkflow) {
        this.isPostStepThreeReadOnlyWorkflow = true;
        this.context.updatePostStepThreeReadOnlyWorkflow(true);
      }
    }
  }

}
